import { COUNTRY_DEFAULT_NAMINGS, VERIDAS_IDV_SUPPORTED_DOCUMENTS } from "./supportedDocuments";
import { getName } from "i18n-iso-countries";
import { useMemo } from "react";
import { useDocumentationArea } from "src/apps/hooks/useDocumentationArea";
import { IdentityVerificationDocumentTypes } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentTypes";
import { AllowedPair } from "src/contexts/identityVerification/domain/AllowedPair";
import { useLocale } from "../useLocale";

/**
 * This hook is used to get the allowed pairs of identity verification
 * documents and countries from Veridas and filter out what is needed
 * from the Documentation Area Identity Verification Request.
 * Replicates the same logic we have in open-admin-app.
 * Should be used through the useIdentityVerification hook.
 * @returns {filteredAllowedPairs} - The filtered allowed pairs of identity verification documents and countries.
 */

export const useIdentityVerificationFilteredPairs = () => {
  const { identityVerifications } = useDocumentationArea();
  const { locale } = useLocale();
  if (!locale) throw Error("Locale is not defined");

  const defaultPairsInfo = {
    correspondences: COUNTRY_DEFAULT_NAMINGS as Record<string, string>,
    id_card: VERIDAS_IDV_SUPPORTED_DOCUMENTS.filter(
      (doc) => doc.endsWith("_ID") && !doc.startsWith("XX") && doc.split("_")[0].length !== 3,
    ).map((doc) => doc.split("_")[0]),
    passport: VERIDAS_IDV_SUPPORTED_DOCUMENTS.filter(
      (doc) => doc.length === 2 && !doc.startsWith("XX"),
    ),
    residence_permit: Array.from(
      new Set(
        VERIDAS_IDV_SUPPORTED_DOCUMENTS.filter(
          (doc) =>
            doc.split("_")[1] === "ResidencePermit" &&
            !doc.startsWith("XX") &&
            doc.split("_")[0].length !== 3,
        ).map((doc) => doc.split("_")[0]),
      ),
    ),
    driver_license: VERIDAS_IDV_SUPPORTED_DOCUMENTS.filter(
      (doc) => doc.endsWith("_DL") && !doc.startsWith("XX") && doc.split("_")[0].length !== 3,
    ).map((doc) => doc.split("_")[0]),
  };

  // Change when we allow more than one
  const identityVerification = identityVerifications[0];

  const allowedCountries: string[] = identityVerification.getRequest().data.allowedCountries;
  const bannedCountries: string[] = identityVerification.getRequest().data.bannedCountries;
  const allowedDocuments: IdentityVerificationDocumentTypes[] =
    identityVerification.getRequest().data.allowedDocuments;
  const bannedDocuments: IdentityVerificationDocumentTypes[] =
    identityVerification.getRequest().data.bannedDocuments;

  const parsePairsInfoToAllowedPairs = (pairsInfo: {
    correspondences: { [k: string]: string };
    driver_license: string[];
    id_card: string[];
    passport: string[];
    residence_permit: string[];
  }): { documentType: IdentityVerificationDocumentTypes; countries: string[] }[] => {
    return Object.entries(pairsInfo)
      .filter(([key]) => key !== "correspondences")
      .map(([key, countries]) => ({
        documentType: key as IdentityVerificationDocumentTypes,
        countries,
      })) as AllowedPair[];
  };

  const allowedCountriesAndDocumentsMergedWithIdentityVerificationRequestData = () => {
    const pairsInfo = { ...defaultPairsInfo };

    const documentTypes = Object.keys(pairsInfo).filter(
      (docType) => docType !== "correspondences",
    ) as IdentityVerificationDocumentTypes[];

    if (allowedCountries.length > 0) {
      documentTypes.forEach((documentType) => {
        pairsInfo[documentType as IdentityVerificationDocumentTypes] = allowedCountries;
      });
    }

    if (allowedDocuments.length > 0) {
      for (const document of documentTypes) {
        if (!allowedDocuments.includes(document as IdentityVerificationDocumentTypes))
          pairsInfo[document as IdentityVerificationDocumentTypes] = [];
      }
    }
    if (bannedCountries.length > 0) {
      documentTypes.forEach((document) => {
        const countries = pairsInfo[document as IdentityVerificationDocumentTypes].filter(
          (country) => !bannedCountries.includes(country),
        );
        pairsInfo[document as IdentityVerificationDocumentTypes] = countries;
      });
    }

    if (bannedDocuments.length > 0) {
      for (const document of bannedDocuments) {
        pairsInfo[document] = [];
      }
    }
    return parsePairsInfoToAllowedPairs(pairsInfo);
  };

  const parseCountries = (countries: string[]) => {
    return countries
      .map((value) => {
        const label = defaultPairsInfo.correspondences[value];
        if (value.includes("-")) {
          const [code] = value.split("-");
          const [, ...labelParts] = label.split(" - ");
          return {
            value,
            label: `${getName(code, locale)} - ${labelParts.join(" - ")}`,
          };
        } else {
          return {
            value,
            label: getName(value, locale) ?? label,
          };
        }
      })
      .sort((a, b) => {
        if (a.value === "ES") return -1;
        else if (b.value === "ES") return 1;
        else return a.label.toUpperCase() > b.label.toUpperCase() ? 1 : -1;
      });
  };

  const filteredAllowedPairs = useMemo(() => {
    const hasCountries =
      allowedCountries.length > 0 ||
      allowedDocuments.length > 0 ||
      bannedCountries.length > 0 ||
      bannedDocuments.length > 0;

    const data = hasCountries
      ? allowedCountriesAndDocumentsMergedWithIdentityVerificationRequestData()
      : [
          {
            documentType: IdentityVerificationDocumentTypes.ID_CARD,
            countries: defaultPairsInfo[IdentityVerificationDocumentTypes.ID_CARD],
          },
          {
            documentType: IdentityVerificationDocumentTypes.PASSPORT,
            countries: defaultPairsInfo[IdentityVerificationDocumentTypes.PASSPORT],
          },
          {
            documentType: IdentityVerificationDocumentTypes.RESIDENCE_PERMIT,
            countries: defaultPairsInfo[IdentityVerificationDocumentTypes.RESIDENCE_PERMIT],
          },
          {
            documentType: IdentityVerificationDocumentTypes.DRIVER_LICENSE,
            countries: defaultPairsInfo[IdentityVerificationDocumentTypes.DRIVER_LICENSE],
          },
        ];

    return data.map((pair) => {
      return {
        documentType: pair.documentType,
        countries: parseCountries(pair.countries),
      };
    });
  }, [locale]);

  return {
    filteredAllowedPairs,
  };
};
